import {
  userInfoQuery,
  projectSearchQuery,
  projectSearchAllQuery,
  projectModelsQuery,
  projectObjectQuery,
  projectWallsObjectsQuery,
  projectChildrenSampleQuery,
  projectModelByName, projectModelViewerResource, projectIssuesQuery, projectIssueRepliesQuery,
  projectBracingWallsQuery
} from "@/speckleQueries"
import { createIssueMutation, createIssueReplyMutation, updateIssueFields } from "@/speckleMutations";
export const APP_NAME = process.env.VUE_APP_SPECKLE_NAME
export const SERVER_URL = process.env.VUE_APP_SERVER_URL
export const QWEBAPI_URL = process.env.QWEBAPI_URL || "http://localhost:4000/"
export const TOKEN = `${APP_NAME}.AuthToken`
export const REFRESH_TOKEN = `${APP_NAME}.RefreshToken`
export const CHALLENGE = `${APP_NAME}.Challenge`

// Redirects to the Speckle server authentication page, using a randomly generated challenge. Challenge will be stored to compare with when exchanging the access code.
export function goToSpeckleAuthPage() {
  // Generate random challenge
  var challenge =
    Math.random()
      .toString(36)
      .substring(2, 15) +
    Math.random()
      .toString(36)
      .substring(2, 15)
  // Save challenge in localStorage
  localStorage.setItem(CHALLENGE, challenge)
  // Send user to auth page
  window.location = `${SERVER_URL}/authn/verify/${process.env.VUE_APP_SPECKLE_ID}/${challenge}`
}

// Log out the current user. This removes the token/refreshToken pair.
export function speckleLogOut() {
  // Remove both token and refreshToken from localStorage
  localStorage.removeItem(TOKEN)
  localStorage.removeItem(REFRESH_TOKEN)
}

// Exchanges the provided access code with a token/refreshToken pair, and saves them to local storage.
export async function exchangeAccessCode(accessCode) {
  var res = await fetch(`${SERVER_URL}/auth/token/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      accessCode: accessCode,
      appId: process.env.VUE_APP_SPECKLE_ID,
      appSecret: process.env.VUE_APP_SPECKLE_SECRET,
      challenge: localStorage.getItem(CHALLENGE)
    })
  })
  var data = await res.json()
  if (data.token) {
    // If retrieving the token was successful, remove challenge and set the new token and refresh token
    localStorage.removeItem(CHALLENGE)
    localStorage.setItem(TOKEN, data.token)
    localStorage.setItem(REFRESH_TOKEN, data.refreshToken)
  }
  return data
}

// Calls the GraphQL endpoint of the Speckle server with a specific query, using the given token.
// Operations whose arguments cannot be interpolated into the query string — JSONObject
// scalars such as an issue description, or any user-typed text containing quotes — pass
// them as GraphQL variables instead.
async function speckleGraphqlRequest(query, token, variables = null) {
  try {
    var res = await fetch(`${SERVER_URL}/graphql`, {
      method: "POST",
      headers: {
        Authorization: "Bearer " + token,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(variables ? {query, variables} : {query})
    })
    return await res.json()
  } catch (err) {
    console.error("API call failed", err)
  }
}

// Calls the GraphQL endpoint of the Speckle server with a specific query.
export async function speckleFetch(query, variables = null) {
  let token = localStorage.getItem(TOKEN)
  if (token) return speckleGraphqlRequest(query, token, variables)
  else return Promise.reject("You are not logged in (token does not exist)")
}

// Calls the GraphQL endpoint of the Speckle server using the app-level token from VUE_APP_SPECKLE_TOKEN instead of the logged-in user's token.
export async function speckleTokenFetch(query, variables = null) {
  const token = process.env.VUE_APP_SPECKLE_TOKEN
  if (token) return speckleGraphqlRequest(query, token, variables)
  // Shaped like a GraphQL failure rather than a rejection: callers check response.errors,
  // and none of them wrap the await, so rejecting here would surface as an unhandled rejection.
  return {errors: [{message: "VUE_APP_SPECKLE_TOKEN is not set"}]}
}

// Fetch the current user data using the userInfoQuery
export const getUserData = () => speckleFetch(userInfoQuery())

export const getProjects = e => speckleFetch(projectSearchQuery(e))

export const getAllProjects = () => speckleFetch(projectSearchAllQuery())

export const getProjectModels = (projectId, itemsPerPage, cursor, includeVersions = true) =>
  speckleFetch(projectModelsQuery(projectId, itemsPerPage, cursor, includeVersions))

export const getProjectModelId = (projectId, modelName) => speckleFetch(projectModelByName(projectId, modelName))

export const getProjectModelViewerResource = (projectId, modelId) => speckleFetch(projectModelViewerResource(projectId, modelId))

export const getProjectObject = (projectId, objectId) => speckleFetch(projectObjectQuery(projectId, objectId))

export const getProjectWallsObjects = (projectId, objectId, language = 'en', limit = 200, cursor = null) => speckleFetch(projectWallsObjectsQuery(projectId, objectId, language, limit, cursor))

export const getProjectBracingWalls = (projectId, objectId, language = 'en', limit = 200, cursor = null) => speckleFetch(projectBracingWallsQuery(projectId, objectId, language, limit, cursor))

export const getProjectChildrenSample = (projectId, objectId, limit = 1) => speckleFetch(projectChildrenSampleQuery(projectId, objectId, limit))

// resourceIdString is a model id for the model-scoped viewer panel, or null for every
// issue in the project.
export const getProjectIssues = (projectId, resourceIdString = null, cursor = null, limit = 25) =>
  speckleTokenFetch(projectIssuesQuery(projectId, resourceIdString, cursor, limit))

export const getIssueReplies = (projectId, issueId, cursor = null, limit = 25) => speckleTokenFetch(projectIssueRepliesQuery(projectId, issueId, cursor, limit))

// The whole Issues surface runs on the app token: Speckle gates it behind the issues:read /
// issues:write scopes, which the OAuth app does not have (its token gets "Your token does not
// have the required scope for this action"), and scopes are fixed at app registration so they
// cannot be widened from here. Consequence: issues and replies are authored by the token's
// account rather than the signed-in user. Restoring real attribution means adding the issues
// scopes to the Speckle app, having users re-authenticate, and moving these back to speckleFetch.

// Speckle treats resourceIdString + viewerState + screenshot as one unit — all three or
// none — so the 3D reference is only attached when a viewer was there to capture it.
// Without it the issue is created as a general project issue.
export const addIssue = (projectId, title, description, threeD = null) =>
  speckleTokenFetch(createIssueMutation(), {
    input: {
      projectId,
      title,
      description,
      ...(threeD?.viewerState && threeD?.screenshot && threeD?.resourceIdString ? threeD : {})
    }
  })

export const replyToIssue = (projectId, issueId, description) =>
  speckleTokenFetch(createIssueReplyMutation(), {input: {projectId, issueId, description}})

export const setCommentStatus = (projectId, commentId, status) => speckleTokenFetch(updateIssueFields(projectId, commentId, {status}))

export const setCommentPriority = (projectId, commentId, priority) => speckleTokenFetch(updateIssueFields(projectId, commentId, {priority}))

// dueDate is either an ISO 8601 string, or null to clear the due date.
export const setCommentDueDate = (projectId, commentId, dueDate) => speckleTokenFetch(updateIssueFields(projectId, commentId, {dueDate}))
